/**
 * How alike two paragraph texts are, on a 0..1 scale, for every place that
 * has to decide whether a paragraph is "the same one, edited": the paragraph
 * locator's tiers on the web route and the add-in's local recovery steps.
 *
 * Two views are taken and the higher wins. The word view is the Jaccard index
 * of the two word sets, which forgives reordering and repeated words. The
 * character view is the ratio of matched characters to total characters over
 * the longest common substrings, found recursively the way `difflib` does it,
 * which forgives a few edited words inside a long paragraph. A reworded clause
 * scores well on characters when the word sets have drifted, and a reordered
 * clause scores well on words when the character order has; either alone
 * missed cases the other caught.
 *
 * The words are the redline diff's units (`WordTokenizer.diffUnits`), compared by
 * `WordTokenizer.comparisonKey`, so a Japanese, Chinese or Thai paragraph has a
 * word view too: cut at whitespace it was one word, and a paragraph whose
 * sentences the user swapped scored on characters alone.
 */
import { WordTokenizer } from "./word-tokenizer";

export class TextSimilarity {
    /**
     * SCOPE
     *   Compares the two paragraph texts as peer inputs: neither side is treated as a base
     *   version.
     *
     * ORDER
     *   `score(a, b)` and `score(b, a)` can differ. `characterRatio` follows difflib's
     *   `ratio()`, which takes one longest common substring and recurses around it,
     *   breaking a length tie toward the block earliest in its first argument — so the
     *   two orders can pick different blocks and reach different totals. `wordJaccard`
     *   is symmetric, so the difference comes from the character view alone.
     *
     *   CONSEQUENCE
     *     A caller scoring candidates against a fixed threshold passes its arguments in
     *     one consistent order, or the same pair can land on either side of it.
     *
     * WHY
     *   The two views normalize differently.
     *     - word view      -> the redline diff's units, each by its comparison key (NFC)
     *     - character view -> raw UTF-16 code units from the original strings
     */
    static score(leftText: string, rightText: string): number {
        return Math.max(wordJaccard(leftText, rightText), characterRatio(leftText, rightText));
        /**
         * Shared words over all distinct words; zero when either text has none.
         *
         * EXAMPLE
         *   "代金は…支払うものとする。遅延した…利息を付す。" against the same two sentences swapped:
         *   the same 23 distinct words on both sides -> 23 / 23 = 1, where the whole runs,
         *   one whitespace word each and different, scored 0 and left the paragraph to the
         *   character view's 0.52
         */
        function wordJaccard(first: string, second: string): number {
            const firstWords = new Set(WordTokenizer.diffUnits(first).map((word) => WordTokenizer.comparisonKey(word.text)));
            const secondWords = new Set(WordTokenizer.diffUnits(second).map((word) => WordTokenizer.comparisonKey(word.text)));
            const firstWordSetIsEmpty = firstWords.size === 0;
            const secondWordSetIsEmpty = secondWords.size === 0;
            /**
             * WHEN either word set is empty
             *   CONSEQUENCE
             *     Return 0: this view found no tokenizable words to overlap.
             *
             * EDGE CASE
             *   Text that is only punctuation or whitespace also ends up here if
             *   `WordTokenizer.words` emits no words for it.
             */
            if (firstWordSetIsEmpty || secondWordSetIsEmpty) return 0;
            let sharedWordCount = 0;
            /**
             * WHY
             *   Counts shared distinct words, not repeated occurrences.
             *
             * EXAMPLE
             *   firstWords  = {"a", "b"}
             *   secondWords = {"b", "c"}
             *   shared here -> 1 for "b"
             */
            for (const leftWord of firstWords) if (secondWords.has(leftWord)) sharedWordCount++;
            return sharedWordCount / (firstWords.size + secondWords.size - sharedWordCount);
        }
        /**
         * `2 * matched / (first.length + second.length)`, where the matched
         * characters are found by taking the longest common substring, then
         * recursing into the text on either side of it, so every match is in order
         * and non-overlapping. Identical texts score 1, texts with nothing in
         * common 0.
         */
        function characterRatio(first: string, second: string): number {
            const totalCharacterCount = first.length + second.length;
            /**
             * WHEN both texts are empty
             *   CONSEQUENCE
             *     Return 1: there are no characters on either side to disagree.
             */
            if (totalCharacterCount === 0) return 1;
            const firstTextIsEmpty = first.length === 0;
            const secondTextIsEmpty = second.length === 0;
            /**
             * WHEN exactly one text is empty
             *   CONSEQUENCE
             *     Return 0: after the both-empty case above, no common substring is possible.
             */
            if (firstTextIsEmpty || secondTextIsEmpty) return 0;

            interface LongestCommonSubstringMatch {
                firstStart: number;
                secondStart: number;
                /**
                 * RETURNS
                 *   Longest common substring of `first[leftStartIndex..leftEndIndex)` and
                 *   `second[rightStartIndex..rightEndIndex)`.
                 *
                 * CASES
                 *   - one match is longer                    -> keep it
                 *   - lengths tie, one starts earlier in `first`  -> keep the earlier `firstStart`
                 *   - lengths tie, same `firstStart`         -> keep the first `secondStart` seen
                 */
                length: number;
            }
            const findLongestCommonSubstringMatch = (leftStartIndex: number, leftEndIndex: number, rightStartIndex: number, rightEndIndex: number): LongestCommonSubstringMatch => {
                let bestMatch: LongestCommonSubstringMatch = { firstStart: leftStartIndex, secondStart: rightStartIndex, length: 0 };
                /**
                 * TYPE
                 *   Map from `secondIndex` to the length of the common-suffix run that ended at
                 *   `(firstIndex - 1, secondIndex)` in the previous row.
                 */
                let substringLengthByRightIndex = new Map<number, number>();
                for (let firstIndex = leftStartIndex; firstIndex < leftEndIndex; firstIndex++) {
                    /**
                     * TYPE
                     *   Same map shape for the current `firstIndex` row.
                     *
                     * WHY
                     *   Only diagonals extended by the current `leftCharacter` are carried forward.
                     */
                    const nextSubstringLengthByRightIndex = new Map<number, number>();
                    const leftCharacter = first[firstIndex];
                    for (let secondIndex = rightStartIndex; secondIndex < rightEndIndex; secondIndex++) {
                        /**
                         * WHEN the current characters differ
                         *   CONSEQUENCE
                         *     Skip this cell: only equal characters can extend a diagonal substring run.
                         */
                        if (second[secondIndex] !== leftCharacter) continue;
                        /**
                         * WHY
                         *   `secondIndex - 1` is the diagonal predecessor: one column left in the
                         *   previous row.
                         *
                         * EXAMPLE
                         *   previous row at secondIndex 6 -> 3
                         *   current  row at secondIndex 7 -> 4 after `+ 1`
                         */
                        const length = (substringLengthByRightIndex.get(secondIndex - 1) ?? 0) + 1;
                        nextSubstringLengthByRightIndex.set(secondIndex, length);
                        /**
                         * CASES
                         *   - `length` is longer than `bestMatch.length` -> replace `bestMatch`
                         *   - lengths tie                                -> keep the earlier winner already stored
                         *
                         * EXAMPLE
                         *   firstIndex = 10, length = 4 -> firstStart  = 7
                         *   secondIndex = 15, length = 4 -> secondStart = 12
                         */
                        if (length > bestMatch.length) bestMatch = { firstStart: firstIndex - length + 1, secondStart: secondIndex - length + 1, length };
                    }
                    substringLengthByRightIndex = nextSubstringLengthByRightIndex;
                }
                return bestMatch;
            };
            /**
             * RETURNS
             *   Matched-character count inside the two half-open ranges.
             *
             * WHY
             *   Take one longest common substring, then recurse only on the unmatched text
             *   to its left and right.
             */
            const countMatchedCharactersInRanges = (leftStartIndex: number, leftEndIndex: number, rightStartIndex: number, rightEndIndex: number): number => {
                const longestSubstringMatch = findLongestCommonSubstringMatch(leftStartIndex, leftEndIndex, rightStartIndex, rightEndIndex);
                /**
                 * WHEN the remaining ranges share no common substring
                 *   CONSEQUENCE
                 *     Stop this branch and contribute 0 more matched characters.
                 */
                if (longestSubstringMatch.length === 0) return 0;
                /**
                 * SCOPE
                 *   Recurse on the unmatched text after the matched block on both sides.
                 *
                 * EXAMPLE
                 *   firstStart = 5, length = 3 -> next left index  = 8
                 *   secondStart = 9, length = 3 -> next right index = 12
                 *   A match covering 5, 6, 7 leaves the right-side recursion starting at 8.
                 */
                return longestSubstringMatch.length
                    + countMatchedCharactersInRanges(leftStartIndex, longestSubstringMatch.firstStart, rightStartIndex, longestSubstringMatch.secondStart)
                    + countMatchedCharactersInRanges(longestSubstringMatch.firstStart + longestSubstringMatch.length, leftEndIndex, longestSubstringMatch.secondStart + longestSubstringMatch.length, rightEndIndex);
            };

            /**
             * RETURNS
             *   Similarity score `2 * matched / (first.length + second.length)`.
             *
             * WHY
             *   Matched characters count once per side, so the numerator doubles them to fit
             *   the combined length denominator.
             *
             * CONSEQUENCE
             *   Identical texts -> 1
             *   More one-sided unmatched text -> lower score
             */
            return (2 * countMatchedCharactersInRanges(0, first.length, 0, second.length)) / totalCharacterCount;
        }
    }
}
